import { z } from "zod";
import { WIKI_TITLE_MAX, type WikiPageType, wikiSlug } from "./wiki.ts";

/**
 * Publish-time OKF checks (openspec change `wiki-okf-structure`, D12): one bounded pass over a
 * create, edit or rename item, run by the API when the item is saved and again at publish, and
 * by the SPA to explain a refusal. Errors break the page or the bundle and refuse the item
 * (`422 wiki_item_invalid`); warnings are shown in review and never block. Linear in the input,
 * which `WIKI_MARKDOWN_MAX` bounds. Unresolved `[[links]]` are a warning too, but need the
 * viewer's read check: the API adds them per viewer (`unresolved_link`).
 */

export const wikiItemErrorSchema = z.enum([
  "title_empty",
  "title_too_long",
  "title_link_syntax",
  "title_line_break",
  "front_matter",
]);
export type WikiItemError = z.infer<typeof wikiItemErrorSchema>;

export const wikiItemWarningSchema = z.object({
  code: z.enum([
    "unresolved_link",
    "heading_repeats_title",
    "heading_level_skipped",
    "summary_without_source",
  ]),
  /** The link's title (`unresolved_link`) or the heading's text (`heading_level_skipped`). */
  target: z.string().optional(),
});
export type WikiItemWarning = z.infer<typeof wikiItemWarningSchema>;

export interface WikiItemCheck {
  errors: WikiItemError[];
  /** Every warning but `unresolved_link`, which depends on who reads the item. */
  warnings: WikiItemWarning[];
}

/** A front-matter fence opening the body (after blank lines): OKF would read it as metadata. */
const FRONT_MATTER = /^\uFEFF?(?:[ \t]*\r?\n)*---[ \t]*(?:\r?\n|$)/;
const LINE_BREAK = /[\r\n\u2028\u2029]/;

/** An ATX heading's level and text, without regular expressions (no backtracking). */
function atxHeading(line: string): { level: number; text: string } | null {
  let at = 0;
  while (at < 3 && line[at] === " ") {
    at++;
  }
  let level = 0;
  while (line[at + level] === "#") {
    level++;
  }
  const after = line[at + level];
  if (level === 0 || level > 6 || (after !== undefined && after !== " " && after !== "\t")) {
    return null;
  }
  let text = line.slice(at + level).trim();
  // An optional closing sequence: spaces, then only `#`.
  let end = text.length;
  while (end > 0 && text[end - 1] === "#") {
    end--;
  }
  if (end === 0 || text[end - 1] === " " || text[end - 1] === "\t") {
    text = text.slice(0, end).trimEnd();
  }
  return { level, text };
}

/** The ATX headings of `markdown` outside fenced code blocks, in order. */
function headings(markdown: string): { level: number; text: string }[] {
  const found: { level: number; text: string }[] = [];
  let fence: string | null = null;
  for (const raw of markdown.split("\n")) {
    const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
    const trimmed = line.trimStart();
    const marker = trimmed.startsWith("```") ? "```" : trimmed.startsWith("~~~") ? "~~~" : null;
    if (marker && line.length - trimmed.length < 4) {
      if (fence === null) {
        fence = marker;
      } else if (fence === marker) {
        fence = null;
      }
      continue;
    }
    if (fence === null) {
      const heading = atxHeading(line);
      if (heading) {
        found.push(heading);
      }
    }
  }
  return found;
}

export function checkWikiItem(input: {
  title: string;
  markdown: string;
  pageType: WikiPageType;
  sourceCount: number;
}): WikiItemCheck {
  const errors: WikiItemError[] = [];
  const title = input.title.trim();
  if (title === "") {
    errors.push("title_empty");
  }
  if (title.length > WIKI_TITLE_MAX) {
    errors.push("title_too_long");
  }
  if (input.title.includes("[[") || input.title.includes("]]")) {
    errors.push("title_link_syntax");
  }
  if (LINE_BREAK.test(input.title)) {
    errors.push("title_line_break");
  }
  if (FRONT_MATTER.test(input.markdown)) {
    errors.push("front_matter");
  }
  const warnings: WikiItemWarning[] = [];
  const found = headings(input.markdown);
  const [first] = found;
  if (first && title !== "" && wikiSlug(first.text) === wikiSlug(title)) {
    warnings.push({ code: "heading_repeats_title" });
  }
  // The title is the page's first level: a body may open at level 1 or 2, then go one deeper.
  let previous = 1;
  for (const heading of found) {
    if (heading.level > previous + 1) {
      warnings.push({ code: "heading_level_skipped", target: heading.text });
      break;
    }
    previous = heading.level;
  }
  if (input.pageType === "summary" && input.sourceCount === 0) {
    warnings.push({ code: "summary_without_source" });
  }
  return { errors, warnings };
}
